import { useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RectAreaLightUniformsLib } from 'three/examples/jsm/lights/RectAreaLightUniformsLib.js';
import type { Projection } from '@/shared/store/viewer-store';
import { cameraFitMargin, fitCameraToBox } from '../lib/geometry-helpers';
import {
  copyCameraPose,
  fitOrthographicCameraToBox,
  isOrthographicProjection,
  projectedBoxRect,
  syncOrthographicFrustum,
  type SceneCamera,
} from '../lib/sceneCameras';
import { narrowFrameReserve } from '../lib/frameReserve';
import {
  canvasReferenceRect,
  frameZoneRects,
  visibleBandFromInsets,
  type FrameInsets,
} from '../lib/viewportInsets';

export type ThreeCtx = {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  /**
   * АКТИВНАЯ камера кадра — та, которой сцена рисуется и по которой считается
   * ВСЁ экранное: выделение, гизмо, линейка, цепочки размеров, HUD.
   *
   * Тип — объединение намеренно. Раньше здесь стояла `PerspectiveCamera`, и
   * поэтому `camera.fov` читался в шести местах без вопросов. Объединение
   * заставляет `tsc` показать КАЖДОГО такого читателя: у ортографии `fov` нет,
   * и молчаливый `undefined` в матрице кадра дал бы NaN, а не отказ.
   */
  camera: SceneCamera;
  /** Фотографическая камера: перспектива и изометрия, трассировщик снимков. */
  perspectiveCamera: THREE.PerspectiveCamera;
  /** Чертёжная камера: план, фронт, бок — по ней можно мерить. */
  orthographicCamera: THREE.OrthographicCamera;
  /**
   * Подписка на СМЕНУ активной камеры. Камера — общий ресурс, и часть её
   * читателей держит ссылку, а не перечитывает поле: `OrbitControls`,
   * `TransformControls` перемещения и гизмо панели получают камеру в
   * конструкторе. Без оповещения они остались бы на прежней камере, и человек
   * тянул бы стрелку, посчитанную по ДРУГОЙ проекции, чем та, что на экране.
   */
  onCameraChanged(listener: (camera: SceneCamera) => void): () => void;
  /**
   * Подписчики живут НА контексте, а не в карте рядом с ним.
   *
   * Сначала они лежали в модульном `WeakMap<ThreeCtx, notify>`, и у этого была
   * тихая беда: контекст, собранный не хуком (а так собраны все проверки),
   * в карте отсутствовал — `activateCamera` молча никого не оповещал, и
   * доказать пересадку гизмо было нечем. Имя ворот было, кода за ними не было.
   */
  cameraListeners: Set<(camera: SceneCamera) => void>;
  controls: OrbitControls;
  /* WALKTHROUGH: пока true, render loop не зовёт controls.update() — тот
   * каждый кадр lookAt(target) и отбирает камеру у первого лица. Мутабельный
   * флаг на ctx, а не состояние React: кадры не должны ждать рендера. */
  walkthroughActive: boolean;
  content: THREE.Group;
  floorGroup: THREE.Group;
  roomGroup: THREE.Group;
  clock: THREE.Clock;
  el: HTMLDivElement;
};

/**
 * Половина высоты ортографического кадра, применённая последней подгонкой.
 * Живёт рядом с контекстом, а не в нём, потому что это не ресурс сцены, а
 * память ОДНОГО вычисления: ресайз холста обязан пересчитать left/right от той
 * же высоты, иначе чертёж растянется, а замер по нему станет неверным.
 */
const orthoHalfHeightMm = new WeakMap<THREE.OrthographicCamera, number>();

export function orthographicHalfHeightMm(camera: THREE.OrthographicCamera): number | null {
  return orthoHalfHeightMm.get(camera) ?? null;
}

/**
 * ПЕРЕКЛЮЧЕНИЕ АКТИВНОЙ КАМЕРЫ — ОДНОЙ РУКОЙ.
 *
 * Меняет `ctx.camera`, переносит позу, пересаживает `OrbitControls` и
 * оповещает всех, кто держит ссылку на камеру (гизмо перемещения, гизмо
 * панели). Возвращает `true`, если камера действительно сменилась: вызывающий
 * не обязан отличать «переключили» от «уже было так».
 *
 * Отдельная функция, а не ветка внутри `applyProjection`, потому что у смены
 * камеры есть второй вызывающий — восстановление сохранённого ракурса.
 */
export function activateCamera(ctx: ThreeCtx, projection: Projection): boolean {
  const wanted: SceneCamera = isOrthographicProjection(projection)
    ? ctx.orthographicCamera
    : ctx.perspectiveCamera;
  if (ctx.camera === wanted) return false;
  copyCameraPose(ctx.camera, wanted);
  ctx.camera = wanted;
  // OrbitControls читает камеру из своего поля `object` на каждом кадре;
  // пересадка без `update()` оставила бы его внутреннюю сферу от прошлой
  // камеры, и первое же движение мыши дёрнуло бы кадр.
  ctx.controls.object = wanted;
  ctx.controls.update();
  // Оповещение — последним: подписчик, разбуженный до пересадки орбиты,
  // прочитал бы старую камеру у контроллера.
  for (const listener of [...ctx.cameraListeners]) listener(wanted);
  return true;
}

/**
 * Подогнать АКТИВНУЮ камеру под коробку — одной рукой для обеих камер.
 *
 * Здесь нельзя обойтись `fitCameraToBox`: у перспективы кадр решает
 * РАССТОЯНИЕ (угол обзора постоянен), у ортографии — РАЗМЕР ФРУСТУМА
 * (расстояние на масштаб не влияет вовсе). Подогнать ортографию перспективной
 * формулой значит отодвинуть камеру и не изменить кадр ни на миллиметр —
 * изделие осталось бы во весь экран или точкой, и человек списал бы это на
 * «зум не работает».
 *
 * Соотношение сторон берётся у ХОЛСТА, а не у камеры: у ортографической
 * камеры поля `aspect` нет вовсе, оно выражено через left/right.
 *
 * ЗДЕСЬ ЖЕ ХОЛСТ ПЕРЕСТАЁТ СЧИТАТЬСЯ ОТКРЫТЫМ ЦЕЛИКОМ.
 *
 * Обе подгонки — перспективная и ортографическая — решали задачу «уместить
 * коробку во ВЕСЬ кадр». Кадр у Luminous закрыт сверху островом навигации и
 * снизу лентой этапов: они лежат НАД сценой, и модель уезжала под них (замер
 * владельца на шкафу: 208 px снизу, 28 % высоты холста). Полоса замеряется
 * ровно в этой точке — единственной, через которую проходят оба вписывания и
 * оба вызывающих (загрузка бандла и смена проекции). Второго места, где
 * «холст считается открытым», быть не должно.
 */
export function fitSceneCameraToBox(
  ctx: ThreeCtx,
  box: THREE.Box3,
  padding: number,
  direction?: THREE.Vector3,
): void {
  const canvas = ctx.renderer.domElement;
  const canvasRect = canvasReferenceRect(canvas);
  // Замер, а не константа: высота ленты объявлена как min-height и растёт от
  // содержимого, а панели и орбиты появляются по состоянию. Вписанное в код
  // число стало бы ложно-зелёным на первой же правке вёрстки.
  //
  // Прямоугольники берутся вместе с ЗАПОМНЕННЫМИ: зона, объявившая себя рамой,
  // сужает кадр и когда она временно снята с экрана. Иначе вписывание, попавшее
  // на открытую панель «Проекция и виды», считало бы раму из одного `top-nav`.
  const rects = frameZoneRects(canvas);
  const applyWithReserve = (insets: FrameInsets) => {
    const band = visibleBandFromInsets(insets, canvasRect.width, canvasRect.height);
    const camera = ctx.camera;
    if (camera instanceof THREE.OrthographicCamera) {
      const w = canvas.clientWidth || canvas.width || 800;
      const h = canvas.clientHeight || canvas.height || 600;
      const frame = fitOrthographicCameraToBox(camera, ctx.controls, box, w / h, padding, direction, band);
      orthoHalfHeightMm.set(camera, frame.halfHeightMm);
    } else {
      fitCameraToBox(camera, ctx.controls, box, padding, direction, band);
      camera.lookAt(ctx.controls.target);
    }
    return projectedBoxRect(box, camera, canvasRect);
  };
  narrowFrameReserve(rects, canvasRect, applyWithReserve);
  rememberLastFit(ctx, { box: box.clone(), padding, direction: direction?.clone() ?? null });
}

/**
 * ПОСЛЕДНЕЕ ВПИСЫВАНИЕ — ЧТОБЫ ЕГО МОЖНО БЫЛО ПОВТОРИТЬ ПРИ СМЕНЕ РАМЫ.
 *
 * Кадр считается по раме, а рама меняется от вещей, о которых вписывание не
 * знает: человек растянул окно, вернулась зона, которой не было. Замер
 * `panel-front-then-resize` показал цену: вписать на 1024×744 и растянуть до
 * 1440×900 — 21,2 % модели под лентой, потому что `onResize` пересобирал
 * фрустум по СТАРОЙ полувысоте и повторного вписывания не делал вовсе.
 *
 * Хранится ЗАПРОС (коробка, поля, направление), а не результат: повторить надо
 * ту же задачу на новой раме, а не подкрутить прошлый ответ.
 */
type FitRequest = { box: THREE.Box3; padding: number; direction: THREE.Vector3 | null };
const lastFit = new WeakMap<ThreeCtx, FitRequest>();

function rememberLastFit(ctx: ThreeCtx, request: FitRequest): void {
  lastFit.set(ctx, request);
  // Новое вписывание — это НОВОЕ решение о кадре, и прошлое «человек сам крутил»
  // к нему не относится. Не сбросить здесь значит запретить пересчёт навсегда
  // после первого же поворота мышью.
  cameraMovedByHand.delete(ctx);
}

/**
 * ВЫБОР ЧЕЛОВЕКА ПЕРЕБИВАТЬ НЕЛЬЗЯ.
 *
 * Пересчёт кадра на смену рамы — помощь, а не власть. Если человек уже сам
 * повернул, отодвинул или сдвинул камеру, он смотрит туда, куда решил
 * смотреть, и вернуть его в «правильный» кадр значит отобрать управление.
 *
 * Признак берётся у `OrbitControls` из события `start` — оно приходит ТОЛЬКО
 * от руки (кнопка мыши, колесо, палец). Событие `change` для этого не годится:
 * его шлёт и `controls.update()` внутри самой подгонки, и тогда вписывание
 * объявляло бы движением камеры само себя.
 */
const cameraMovedByHand = new WeakMap<ThreeCtx, true>();

export function markCameraMovedByHand(ctx: ThreeCtx): void {
  cameraMovedByHand.set(ctx, true);
}

export function cameraWasMovedByHand(ctx: ThreeCtx): boolean {
  return cameraMovedByHand.get(ctx) === true;
}

/**
 * Повторить последнее вписывание на ТЕКУЩЕЙ раме.
 *
 * @returns было ли что повторять.
 */
export function refitSceneCameraToFrame(ctx: ThreeCtx): boolean {
  const request = lastFit.get(ctx);
  if (!request) return false;
  fitSceneCameraToBox(ctx, request.box, request.padding, request.direction ?? undefined);
  return true;
}

let rectInited = false;
let contactShadowTexture: THREE.Texture | null = null;
let studioFloorMaterial: THREE.MeshStandardMaterial | null = null;
let studioWallMaterial: THREE.MeshStandardMaterial | null = null;
let studioWallAccentMaterial: THREE.MeshStandardMaterial | null = null;
function ensureRectAreaInit() {
  if (rectInited) return;
  RectAreaLightUniformsLib.init();
  rectInited = true;
}

function getContactShadowTexture() {
  if (contactShadowTexture) return contactShadowTexture;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 256;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(128, 128, 10, 128, 128, 122);
  g.addColorStop(0, 'rgba(18, 22, 30, 0.48)');
  g.addColorStop(0.46, 'rgba(18, 22, 30, 0.24)');
  g.addColorStop(1, 'rgba(18, 22, 30, 0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  contactShadowTexture = new THREE.CanvasTexture(canvas);
  contactShadowTexture.needsUpdate = true;
  return contactShadowTexture;
}

/**
 * P0-4 (render-audit 01.10): КОНТАКТ «ПОЛ ↔ ЦОКОЛЬ» ПОД КАЖДЫМ МОДУЛЕМ.
 *
 * Один радиальный планшет на всю сцену покрывал центр и растворялся к краям —
 * у ног отдельного шкафа затемнения не было, и мебель «парила» (диагноз
 * аудита). Под каждым module-владельцем кладётся СВОЙ AO-планшет по его
 * XZ-габариту: плотный у цоколя, мягкий к краям. Прозрачность не зависит от
 * VSM-карты и живёт даже на тирах с выключенными тенями.
 */
export function rebuildModuleContactShadows(ctx: ThreeCtx, root: THREE.Object3D) {
  const floor = ctx.floorGroup.getObjectByName('floor-plane') as THREE.Mesh | undefined;
  const floorY = floor ? floor.position.y : 0;
  const group = ctx.roomGroup;
  const previous = group.children.filter((child) => child.name === 'module-contact-shadow');
  const seen = new Set<string>();
  root.updateWorldMatrix(true, true);
  root.traverse((child) => {
    if (child.type !== 'Mesh' || !child.userData?.moduleId) return;
    const moduleId = String(child.userData.moduleId);
    if (seen.has(moduleId)) return;
    seen.add(moduleId);
    // XZ-габарит модуля в мире
    const bbox = new THREE.Box3().setFromObject(child.parent ?? child);
    const sizeX = Math.max(300, bbox.max.x - bbox.min.x);
    const sizeZ = Math.max(300, bbox.max.z - bbox.min.z);
    const cx = (bbox.max.x + bbox.min.x) / 2;
    const cz = (bbox.max.z + bbox.min.z) / 2;
    const existing = previous.find((p) => (p as THREE.Mesh).userData?.moduleId === moduleId);
    let plane = existing as THREE.Mesh | undefined;
    if (!plane) {
      plane = new THREE.Mesh(
        new THREE.PlaneGeometry(1, 1),
        new THREE.MeshBasicMaterial({
          map: getContactShadowTexture(),
          transparent: true,
          depthWrite: false,
        }),
      );
      plane.name = 'module-contact-shadow';
      plane.rotation.x = -Math.PI / 2;
      plane.userData.moduleId = moduleId;
      group.add(plane);
    }
    plane.scale.set(sizeX * 1.18, sizeZ * 1.18, 1);
    plane.position.set(cx, floorY + 0.9, cz);
    (plane.material as THREE.MeshBasicMaterial).opacity = 0.34;
  });
  // Планшеты модулей, которых больше нет, убираются.
  for (const p of previous) {
    if (!seen.has(String((p as THREE.Mesh).userData?.moduleId))) group.remove(p);
  }
}

function getStudioFloorMaterial(side = 7200) {
  if (studioFloorMaterial) {
    syncStudioFloorRepeat(studioFloorMaterial, side);
    return studioFloorMaterial;
  }
  const map = new THREE.TextureLoader().load('/textures/studio_floor_concrete_f186.jpg');
  map.wrapS = map.wrapT = THREE.RepeatWrapping;
  (map as any).colorSpace = (THREE as any).SRGBColorSpace;
  map.anisotropy = 12;
  const mat = new THREE.MeshStandardMaterial({
    color: '#d4d0c7',
    map,
    bumpMap: map,
    bumpScale: 5.5,
    roughness: 0.88,
    metalness: 0.0,
  });
  studioFloorMaterial = mat;
  syncStudioFloorRepeat(mat, side);
  return mat;
}

function syncStudioFloorRepeat(mat: THREE.MeshStandardMaterial, side: number) {
  const repeat = Math.max(1.35, Math.min(4.2, side / 1800));
  for (const tex of new Set([mat.map, mat.bumpMap])) {
    if (!tex) continue;
    tex.repeat.set(repeat, repeat);
  }
}

function getStudioWallMaterial() {
  if (studioWallMaterial) return studioWallMaterial;
  studioWallMaterial = new THREE.MeshStandardMaterial({
    color: '#d2d7de',
    roughness: 0.95,
    metalness: 0,
    side: THREE.DoubleSide,
  });
  return studioWallMaterial;
}

function getStudioWallAccentMaterial() {
  if (studioWallAccentMaterial) return studioWallAccentMaterial;
  studioWallAccentMaterial = new THREE.MeshStandardMaterial({
    color: '#c7ced6',
    roughness: 0.88,
    metalness: 0,
    side: THREE.DoubleSide,
  });
  return studioWallAccentMaterial;
}

function clearGroupChildren(group: THREE.Group) {
  while (group.children.length) {
    const child = group.children.pop()!;
    child.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.geometry?.dispose?.();
    });
    child.parent?.remove(child);
  }
}

/**
 * Three r166 assumes WebGL always returns a string from get*InfoLog(). Chrome
 * may return null after a successful compile, so its optional diagnostics can
 * throw before the first frame. Keep shadow rendering enabled and skip only
 * that unsafe diagnostics read.
 */
export function preventNullShaderLogCrash(renderer: THREE.WebGLRenderer) {
  renderer.debug.checkShaderErrors = false;
}

export function createViewerRenderer(width: number, height: number): THREE.WebGLRenderer {
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    // Leave the neutral studio background to the Luminous DOM layer.  A solid
    // WebGL clear colour made the space behind a model black even though it is
    // not part of the room or of the material/render contract.
    alpha: true,
    preserveDrawingBuffer: false,
  });
  renderer.setSize(width, height, true);
  if (typeof renderer.setClearAlpha === 'function') {
    renderer.setClearAlpha(0);
  } else if (typeof renderer.setClearColor === 'function') {
    renderer.setClearColor(0x000000, 0);
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.domElement.style.display = 'block';
  renderer.domElement.style.width = '100%';
  renderer.domElement.style.height = '100%';
  (renderer as any).outputColorSpace = (THREE as any).SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.12;  // 30.09: дефолт был пересушен — сцена читалась выцветшей
  // VIS-25: паспорт цвета в рендерере настраивается эффектом ниже
  // (useCatalogColorPass): ACES для сцены, «как в каталоге» — без него.
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.shadowMap.autoUpdate = true;
  preventNullShaderLogCrash(renderer);
  renderer.localClippingEnabled = true;
  return renderer;
}

/** VIS-25: «как в каталоге» = цвет свотча прайса читается буквально.
 * ACES-тонмаппинг красиво катит контраст сцены, но смещает цветовой тон:
 * образец «Дуб Тонсберг» на экране ≠ образец в каталоге. Neutral-режим
 * гасит тонмаппинг и экспозицию к 1.0 — материал на экране совпадает с
 * sRGB-образцом прайса. Возвращает предыдущие значения для отката. */
export function applyCatalogColorPass(renderer: THREE.WebGLRenderer, catalog: boolean): { wasCatalog: boolean; exposure: number } {
  const wasCatalog = renderer.toneMapping === (THREE as any).NoToneMapping;
  const exposure = renderer.toneMappingExposure;
  if (catalog) {
    renderer.toneMapping = (THREE as any).NoToneMapping;
    renderer.toneMappingExposure = 1.0;
  } else {
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.12;
  }
  return { wasCatalog, exposure };
}

export function useThreeScene(container: React.RefObject<HTMLDivElement | null>): ThreeCtx | null {
  const [ctx, setCtx] = useState<ThreeCtx | null>(null);

  useEffect(() => {
    const el = container.current;
    if (!el) return;
    const width = el.clientWidth || 800;
    const height = el.clientHeight || 600;

    const renderer = createViewerRenderer(width, height);
    el.appendChild(renderer.domElement);
    ensureRectAreaInit();

    const scene = new THREE.Scene();
    // The canvas is intentionally transparent until the user explicitly turns
    // on HDRI-as-background. The Luminous stage owns the neutral backdrop.
    scene.background = null;
    scene.fog = new THREE.Fog('#d5d9d4', 9000, 18000);

    // Premium studio floor: matte concrete with a very quiet technical grid.
    const floorGroup = new THREE.Group();
    floorGroup.name = 'floor-shell';
    const grid = new THREE.GridHelper(7000, 28, 0x8f8c83, 0xd4d0c8);
    grid.name = 'floor-grid';
    grid.position.y = -2;
    (grid.material as THREE.Material).transparent = true;
    (grid.material as THREE.Material).opacity = 0.035;
    floorGroup.add(grid);
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(7200, 7200),
      getStudioFloorMaterial(7200),
    );
    floor.name = 'floor-plane';
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -4;
    floor.receiveShadow = true;
    floorGroup.add(floor);
    scene.add(floorGroup);
    // 29.09.2026 — премиум-сетка пола: тёплая, деликатная, с прозрачностью
    const premiumFloorGrid = new THREE.GridHelper(30000, 60, 0xc4bfb6, 0xd5d0c8);
    premiumFloorGrid.material.transparent = true;
    premiumFloorGrid.material.opacity = 0.15;
    premiumFloorGrid.position.y = 0;
    floorGroup.add(premiumFloorGrid);
    const roomGroup = new THREE.Group();
    roomGroup.name = 'room-shell';
    roomGroup.visible = false;
    scene.add(roomGroup);

    const perspectiveCamera = new THREE.PerspectiveCamera(45, width / height, 1, 50000);
    perspectiveCamera.position.set(1800, 1200, 1800);
    // Чертёжная камера рождается рядом с фотографической, а не по требованию:
    // создание камеры в момент переключения вида означало бы, что первый кадр
    // плана рисуется камерой, у которой ещё нет ни кадра, ни near/far.
    const orthographicCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 1, 50000);
    orthographicCamera.position.copy(perspectiveCamera.position);
    syncOrthographicFrustum(orthographicCamera, 1800, width / height);
    orthoHalfHeightMm.set(orthographicCamera, 1800);
    const camera: SceneCamera = perspectiveCamera;

    const cameraListeners = new Set<(next: SceneCamera) => void>();

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.screenSpacePanning = true;

    const content = new THREE.Group();
    content.name = 'content';
    scene.add(content);

    const clock = new THREE.Clock();

    const onResize = () => {
      const w = el.clientWidth || 800;
      const h = el.clientHeight || 600;
      renderer.setSize(w, h, true);
      perspectiveCamera.aspect = w / h;
      perspectiveCamera.updateProjectionMatrix();
      // У ортографии соотношение сторон живёт в left/right, а не в поле
      // `aspect`. Забытый здесь пересчёт растянул бы чертёж по горизонтали —
      // картинка выглядела бы правдоподобно, а замер по ней был бы неверен.
      const halfHeight = orthoHalfHeightMm.get(orthographicCamera);
      if (halfHeight) syncOrthographicFrustum(orthographicCamera, halfHeight, w / h);
    };
    window.addEventListener('resize', onResize);
    const ro = new ResizeObserver(onResize);
    ro.observe(el);
    // second-pass resize after the first layout so initial clientWidth fallbacks
    // (when the container hasn't been measured yet) don't lock-in a stale canvas size
    requestAnimationFrame(() => requestAnimationFrame(onResize));

    const next: ThreeCtx = {
      renderer,
      scene,
      camera,
      perspectiveCamera,
      orthographicCamera,
      cameraListeners,
      onCameraChanged: (listener) => {
        cameraListeners.add(listener);
        return () => cameraListeners.delete(listener);
      },
      controls,
      content,
      floorGroup,
      roomGroup,
      clock,
      el,
      walkthroughActive: false,
    };
    // `start` приходит только от руки — с него и начинается «человек сам
    // двигал камеру», после чего автоматический пересчёт кадра умолкает.
    const onControlsStart = () => markCameraMovedByHand(next);
    controls.addEventListener('start', onControlsStart);
    setCtx(next);

    return () => {
      controls.removeEventListener('start', onControlsStart);
      window.removeEventListener('resize', onResize);
      ro.disconnect();
      cameraListeners.clear();
      controls.dispose();
      renderer.dispose();
      renderer.forceContextLoss?.();
      if (renderer.domElement.parentElement === el) el.removeChild(renderer.domElement);
      setCtx(null);
    };
  }, [container]);

  return ctx;
}

/** Robust "bottom" Y for content. Default behaviour: take the absolute minimum
 *  (so base cabinets / floor-standing modules anchor the floor). Only fall back
 *  to the 5th percentile when the lowest cluster is a CLEAR outlier — i.e. a
 *  big gap (≥600mm) between bottom[0] and the 5th percentile. That handles
   *  PDF parser leftovers and stray dimension helpers without lifting the floor above
 *  real furniture (which was the previous bug — upper cabinets in a kitchen
 *  outnumber base cabinets, so p5 picked the bottom-of-upper-cabinet level
 *  and the floor cut through the gap, leaving base modules visually below it). */
function robustContentFloor(ctx: ThreeCtx): number | null {
  const bottoms: number[] = [];
  const tmp = new THREE.Box3();
  ctx.content.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || m.visible === false) return;
    tmp.setFromObject(m);
    if (Number.isFinite(tmp.min.y)) bottoms.push(tmp.min.y);
  });
  if (!bottoms.length) return null;
  bottoms.sort((a, b) => a - b);
  const lowest = bottoms[0];
  const pIdx = Math.max(1, Math.min(bottoms.length - 1, Math.floor(bottoms.length * 0.05)));
  const p5 = bottoms[pIdx];
  const STRAY_GAP_MM = 600;
  const result = p5 - lowest > STRAY_GAP_MM ? p5 : lowest;
  try {
    // Y-bucket histogram (500mm buckets) so we can detect "two-cluster" scenes
    // where helpers/hardware live far below the actual furniture body.
    const hist: Record<string, number> = {};
    for (const y of bottoms) {
      const b = Math.floor(y / 500) * 500;
      hist[String(b)] = (hist[String(b)] || 0) + 1;
    }
    (window as unknown as { __HOLZCOM_FLOOR_DEBUG?: unknown }).__HOLZCOM_FLOOR_DEBUG = {
      total: bottoms.length,
      lowest,
      p5,
      gap: p5 - lowest,
      chosen: result,
      sample: bottoms.slice(0, 12),
      top: bottoms.slice(-8),
      histogram: Object.entries(hist).sort(([a], [b]) => Number(a) - Number(b)),
    };
  } catch {
    /* noop */
  }
  return result;
}

export function rebuildFloor(ctx: ThreeCtx, box: THREE.Box3) {
  const group = ctx.floorGroup;
  const center = new THREE.Vector3();
  const size = new THREE.Vector3();
  box.getCenter(center);
  box.getSize(size);
  const robust = robustContentFloor(ctx);
  // Prefer robust percentile; fall back to box.min.y if traversal yields nothing.
  let floorY = (robust ?? box.min.y) - 0.8;
  // Guard against parser outliers that push a few helper points far below the real model.
  if (box.min.y < -80 && box.max.y > 400 && size.y > 500 && (robust ?? box.min.y) < -80) {
    floorY = -0.8;
  }
  const side = Math.max(size.x, size.z, 4200) * 1.45;
  const gridDivisions = Math.max(18, Math.round(side / 250));

  const oldGrid = group.getObjectByName('floor-grid');
  if (oldGrid) {
    group.remove(oldGrid);
    (oldGrid as THREE.GridHelper).geometry?.dispose();
  }
  const grid = new THREE.GridHelper(side, gridDivisions, 0x8f8c83, 0xd4d0c8);
  grid.name = 'floor-grid';
  grid.position.set(center.x, floorY + 0.65, center.z);
  (grid.material as THREE.Material).transparent = true;
  (grid.material as THREE.Material).opacity = 0.035;
  group.add(grid);

  const floor = group.getObjectByName('floor-plane') as THREE.Mesh | undefined;
  if (floor) {
    floor.geometry.dispose();
    floor.geometry = new THREE.PlaneGeometry(side, side);
    floor.material = getStudioFloorMaterial(side);
    floor.position.set(center.x, floorY, center.z);
  }

  let shadow = group.getObjectByName('floor-contact-shadow') as THREE.Mesh | undefined;
  const shadowSide = Math.max(900, Math.min(side * 0.62, Math.max(size.x, size.z) * 1.9));
  if (!shadow) {
    shadow = new THREE.Mesh(
      new THREE.PlaneGeometry(shadowSide, shadowSide),
      new THREE.MeshBasicMaterial({
        map: getContactShadowTexture(),
        transparent: true,
        opacity: 0.62,
        depthWrite: false,
      }),
    );
    shadow.name = 'floor-contact-shadow';
    shadow.rotation.x = -Math.PI / 2;
    group.add(shadow);
  } else {
    shadow.geometry.dispose();
    shadow.geometry = new THREE.PlaneGeometry(shadowSide, shadowSide);
  }
  shadow.position.set(center.x, floorY + 1.1, center.z);
}

export function rebuildRoomShell(
  ctx: ThreeCtx,
  box: THREE.Box3,
  opts?: { enabled?: boolean },
) {
  const enabled = opts?.enabled ?? true;
  const group = ctx.roomGroup;
  if (!enabled) {
    group.visible = false;
    clearGroupChildren(group);
    return;
  }

  const center = new THREE.Vector3();
  const size = new THREE.Vector3();
  box.getCenter(center);
  box.getSize(size);
  if (!Number.isFinite(size.x) || !Number.isFinite(size.y) || !Number.isFinite(size.z)) {
    group.visible = false;
    clearGroupChildren(group);
    return;
  }

  const floor = ctx.floorGroup.getObjectByName('floor-plane') as THREE.Mesh | undefined;
  const floorY = floor ? floor.position.y : box.min.y - 0.8;
  const roomPaddingX = Math.max(820, size.x * 0.36);
  const roomPaddingZ = Math.max(760, size.z * 0.34);
  const roomW = Math.max(3800, size.x + roomPaddingX * 2);
  const roomD = Math.max(3200, size.z + roomPaddingZ * 2);
  const roomH = Math.max(2500, Math.min(4200, size.y * 1.18));
  const backZ = center.z - roomD * 0.5;
  const leftX = center.x - roomW * 0.5;

  clearGroupChildren(group);

  const wallMat = getStudioWallMaterial();
  const accentMat = getStudioWallAccentMaterial();
  const backWall = new THREE.Mesh(new THREE.PlaneGeometry(roomW, roomH), wallMat);
  backWall.position.set(center.x, floorY + roomH * 0.5, backZ);
  backWall.receiveShadow = true;
  group.add(backWall);

  const leftWall = new THREE.Mesh(new THREE.PlaneGeometry(roomD, roomH), wallMat);
  leftWall.rotation.y = Math.PI / 2;
  leftWall.position.set(leftX, floorY + roomH * 0.5, center.z);
  leftWall.receiveShadow = true;
  group.add(leftWall);

  const cornerAccent = new THREE.Mesh(new THREE.BoxGeometry(12, roomH, 12), accentMat);
  cornerAccent.position.set(leftX + 6, floorY + roomH * 0.5, backZ + 6);
  group.add(cornerAccent);

  group.visible = true;
}

export function applyProjection(
  ctx: ThreeCtx,
  projection: Projection,
  bounds: { center: THREE.Vector3; size: THREE.Vector3 },
  frontCameraZ: 1 | -1 = 1,
) {
  // Камера выбирается ДО направления: плоский вид — рабочая поверхность, и
  // рисовать её обязана ортография, иначе подпись «развёртка» обещает чертёж,
  // которого нет.
  activateCamera(ctx, projection);
  const { camera, controls } = ctx;
  const c = bounds.center;
  const s = bounds.size;

  // Every projection picks a viewing DIRECTION; the distance is then solved by
  // the one shared fitter.  Previously each branch multiplied `maxDim` by its
  // own constant, and `useGltfLoader` fitted the same scene independently —
  // the two drifted, and since this effect runs last it silently overrode the
  // loader's fit.  A kitchen's `maxDim` is its 3.6 m depth, so scaling by it
  // pushed the camera back for extent the viewer never sees, leaving the
  // furniture at roughly half the frame.
  const direction = new THREE.Vector3();
  switch (projection) {
    case 'perspective':
      direction.set(0.82, 0.58, 0.95 * frontCameraZ);
      break;
    case 'iso':
      direction.set(1, 1, frontCameraZ);
      break;
    case 'top':
      direction.set(0, 1, 0.0001);
      break;
    case 'front':
      direction.set(0, 0, frontCameraZ);
      break;
    case 'side':
      direction.set(1, 0, 0);
      break;
  }

  const box = new THREE.Box3(
    new THREE.Vector3(c.x - s.x / 2, c.y - s.y / 2, c.z - s.z / 2),
    new THREE.Vector3(c.x + s.x / 2, c.y + s.y / 2, c.z + s.z / 2),
  );
  fitSceneCameraToBox(ctx, box, cameraFitMargin(), direction);
  if (camera instanceof THREE.PerspectiveCamera) {
    camera.lookAt(c);
    controls.update();
  }
}
